import { defineChatActions } from './actionsContext'
import { shouldSuppressAutoReadReceipt } from './runtime'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { logger } from '@/utils/eventLogger'
import type { IChat, IChatStoreState, IChatTopic } from '@/modules/chat-matrix-fast/types/IChat'
import {
  isEventChatItem,
  isGroupLikeChatType,
  preserveTotalMembers,
} from '@/modules/chat-matrix-fast/constants/chat'
import { ChatApiService } from '@/modules/chat-matrix-fast/services/chatApiService'
import {
  buildTopicsUnreadDebugBreakdown,
  countMatrixRoomUnread,
  findGeneralTopic,
  isTopicOpenable,
  isTopicsModeChat,
  isTopicsTabsDisplay,
  resolveActiveMessageRoomId,
  sameChatId,
  topicBelongsToChat,
} from '@/modules/chat-matrix-fast/utils/topics'
import {
  fetchChatFromMatrix,
  isSyntheticMatrixChatId,
  peekMatrixRoomMessages,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { subscribeMatrixRoomTimeline } from '@/modules/chat-matrix-fast/services/matrixEventSubscriptions'
import { ensureMatrixChatMembership } from '@/modules/chat-matrix-fast/services/ensureMatrixChatMembership'
import {
  isForgottenRoom,
  restoreForgottenRoom,
} from '@/modules/chat-matrix-fast/services/matrixForgottenRooms'

import {
  syncViewWithCore,
} from '@/modules/chat-matrix-fast/stores/chatStoreSync'
import {
  clearMessages,
  replaceActiveMessages,
  restoreRoomMessages,
} from '@/modules/chat-matrix-fast/stores/core/messageRoomCache'
import {
  clearMatrixSubscription,
  setMatrixSubscription,
} from '@/modules/chat-matrix-fast/stores/matrixSubscriptions'
import { setChatDebugLaneFromChat, summarizeTopicForLog, topicsDebugLog } from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import { chatOpenJumpLog, summarizeJumpMessage } from '@/modules/chat-matrix-fast/utils/chatOpenJumpLog'
import { logEventChat } from '@/modules/chat-matrix-fast/utils/eventChatDebug'

/** Newer openTopic cancels older ensure/fetch (topic switch / nested getChat). */
let openTopicGeneration = 0

/** Инкремент на каждый openChat — отсекает устаревшие await после быстрого переключения. */
let openChatRequestSeq = 0

function paintRoomTimelineFromMemory(
  state: IChatStoreState,
  roomId: string | null | undefined,
): boolean {
  if (restoreRoomMessages(state, roomId)) {
    chatOpenJumpLog('open · paint cache', {
      roomId,
      count: state.messages.length,
      first: summarizeJumpMessage(state.messages[0]),
      last: summarizeJumpMessage(state.messages[state.messages.length - 1]),
    })
    return true
  }

  const sdkMessages = peekMatrixRoomMessages(roomId)
  if (sdkMessages.length === 0) {
    chatOpenJumpLog('open · paint miss', { roomId })
    return false
  }

  replaceActiveMessages(state, sdkMessages, roomId)
  chatOpenJumpLog('open · paint sdk peek', {
    roomId,
    count: state.messages.length,
    first: summarizeJumpMessage(state.messages[0]),
    last: summarizeJumpMessage(state.messages[state.messages.length - 1]),
  })
  return true
}

export const openChatActions = defineChatActions({
  subscribeActiveRoomTimeline(): void {
    const roomId = resolveActiveMessageRoomId(this.$state)
    if (!roomId) {
      clearMatrixSubscription('room')
      return
    }

    setMatrixSubscription('room', subscribeMatrixRoomTimeline(roomId, messages => {
      if (resolveActiveMessageRoomId(this.$state) !== roomId) {
        return
      }
      this.applyRealtimeMessages(messages)
    }))
  },

  // Открывает чат: для topics — сразу «Общий»; иначе лента сообщений
  async openChat(
    chat: IChat,
    options?: { preferTopicsList?: boolean },
  ): Promise<void> {
    const requestSeq = ++openChatRequestSeq
    const isOpenChatStale = () => requestSeq !== openChatRequestSeq
    const previousRoomId = resolveActiveMessageRoomId(this.$state)

    setChatDebugLaneFromChat(chat)
    topicsDebugLog('core · openChat · start', {
      chatId: chat.id,
      chatType: chat.type,
      discussionMode: chat.discussion_mode,
      topicsCount: chat.topics?.length ?? 0,
      currentChatId: this.currentChat?.id ?? null,
      activeTopic: summarizeTopicForLog(this.activeTopic),
      showTopicsList: this.showTopicsList,
      messagesCount: this.messages.length,
      requestSeq,
    })
    logEventChat('open · start', chat, { requestSeq, preferTopicsList: options?.preferTopicsList })

    if (chat.type === 'space') {
      topicsDebugLog('core · openChat · skip (space)')
      return
    }

    const openingChatId = chat.id
    const isSameTopicsChatOpen = this.currentChat?.id === openingChatId
        && isTopicsModeChat(chat)
    const activeTopicForOpeningChat = topicBelongsToChat(this.activeTopic, chat)
      || topicBelongsToChat(
        this.activeTopic,
        this.chats.find(item => sameChatId(item.id, openingChatId)),
      )

    if (
      isSameTopicsChatOpen
        && this.activeTopic?.matrix_room_id
        && activeTopicForOpeningChat
    ) {
      // nest openTopic here and race the in-flight forceRepair/fetch.
      topicsDebugLog('core · openChat · early return (same topics chat + activeTopic)', {
        chatId: openingChatId,
        activeTopic: summarizeTopicForLog(this.activeTopic),
        messagesCount: this.messages.length,
      })
      if (!isSyntheticMatrixChatId(openingChatId, { matrix_room_id: chat.matrix_room_id })) {
        void this.refreshChatMemberCount(openingChatId)
      }
      return
    }

    if (isTopicsModeChat(chat) && this.activeTopic && !activeTopicForOpeningChat) {
      topicsDebugLog('core · openChat · drop stale activeTopic on switch', {
        chatId: openingChatId,
        staleTopic: summarizeTopicForLog(this.activeTopic),
      })
      this.activeTopic = null
      clearMessages(this, { preserveRoomId: previousRoomId })
      const tabs = isTopicsTabsDisplay(chat)
      this.showTopicsList = !tabs
      this.selectedTopicsGroupId = tabs ? null : chat.id
    }

    if (
      this.currentChat?.id === chat.id
        && this.messages.length > 0
        && !isTopicsModeChat(chat)
        && !this.showTopicsList
    ) {
      if (this.activeTopic) {
        this.activeTopic = null
      }

      if (!this.currentChat.matrix_room_id?.trim()) {
        const roomId = chat.matrix_room_id?.trim()
            || this.chats.find(item => item.id === chat.id)?.matrix_room_id?.trim()
        if (roomId) {
          this.currentChat = { ...this.currentChat, matrix_room_id: roomId }
        }
      }

      topicsDebugLog('core · openChat · early return (already open with messages)')
      const earlyType = this.currentChat.type
      if (
        (isGroupLikeChatType(earlyType) || isEventChatItem(this.currentChat))
          && !isSyntheticMatrixChatId(chat.id, { matrix_room_id: this.currentChat.matrix_room_id })
      ) {
        void this.refreshChatMemberCount(chat.id)
      }
      return
    }

    if (isTopicsModeChat(chat)) {
      const optimistic = isSameTopicsChatOpen && this.currentChat
        ? {
            ...this.currentChat,
            ...chat,
            total_members: preserveTotalMembers(chat.total_members, this.currentChat.total_members),
          }
        : chat
      this.setCurrentChatPreservingMemberCount(optimistic)
      topicsDebugLog('core · openChat · optimistic currentChat (topics)', {
        chatId: this.currentChat!.id,
        isSameTopicsChatOpen,
      })
    }

    if (isTopicsModeChat(chat) && !isSameTopicsChatOpen) {
      const tabs = isTopicsTabsDisplay(chat)
      this.showTopicsList = !tabs
      this.selectedTopicsGroupId = tabs ? null : chat.id
      this.activeTopic = null
      clearMessages(this, { preserveRoomId: previousRoomId })
      topicsDebugLog('core · openChat · reset navigation (switch topics group)')
    } else if (isTopicsModeChat(chat) && !this.activeTopic) {
      const tabs = isTopicsTabsDisplay(chat)
      this.showTopicsList = !tabs
      this.selectedTopicsGroupId = tabs ? null : chat.id
      clearMessages(this, { preserveRoomId: previousRoomId })
      topicsDebugLog('core · openChat · reset messages (topics, no activeTopic)')
    }

    let activeChat = chat
    const preferTopicsListEarly = Boolean(options?.preferTopicsList)
        && !isTopicsTabsDisplay(chat)

    if (preferTopicsListEarly) {
      // Mobile list: не ждём fetchChat — панель тем уже открыта из кэша
      this.setCurrentChatPreservingMemberCount(
        isSameTopicsChatOpen && this.currentChat
          ? { ...this.currentChat, ...chat }
          : chat,
      )
      this.selectedTopicsGroupId = chat.id
      this.showTopicsList = true
      this.activeTopic = null
      this.pendingReactionOverrides = {}
      clearMatrixSubscription('room')
      this.syncTopicsUnreadFromMatrix(chat.id)
      syncViewWithCore()

      void this.fetchChat(chat.id).then((fetched: IChat) => {
        if (isOpenChatStale() || this.currentChat?.id !== chat.id) {
          return
        }
        const merged = {
          ...fetched,
          discussion_mode: 'topics' as const,
          topics: fetched.topics?.length ? fetched.topics : (this.currentChat?.topics ?? chat.topics ?? []),
        }
        this.setCurrentChatPreservingMemberCount(merged)
        this.upsertChatInList(merged)
        this.syncTopicsUnreadFromMatrix(merged.id)
        syncViewWithCore()
      }).catch(() => {
        // keep cached
      })

      void ChatApiService.fetchTopics(chat.id).then(topics => {
        if (isOpenChatStale() || this.currentChat?.id !== chat.id) {
          return
        }
        if (topics.length > 0 || !this.currentChat.topics?.length) {
          const updated = {
            ...this.currentChat,
            topics,
            discussion_mode: 'topics' as const,
          }
          this.setCurrentChatPreservingMemberCount(updated)
          this.upsertChatInList(updated)
          this.syncTopicsUnreadFromMatrix(updated.id)
          syncViewWithCore()
        }
      }).catch(() => {
        // keep cached
      })

      topicsDebugLog('core · openChat · done (preferTopicsList, non-blocking)', {
        chatId: chat.id,
        topicsCount: chat.topics?.length ?? 0,
      })
      return
    }

    const cachedRoomId = chat.matrix_room_id?.trim()
        || this.chats.find(item => sameChatId(item.id, chat.id))?.matrix_room_id?.trim()
    const canOpenWithoutAwaitingFetchChat = Boolean(cachedRoomId)

    if (canOpenWithoutAwaitingFetchChat) {
      // Room уже известен — не блокируем ленту на REST/Matrix merge.
      activeChat = {
        ...chat,
        matrix_room_id: cachedRoomId,
      }
      topicsDebugLog('core · openChat · skip await fetchChat (cached room)', {
        chatId: chat.id,
        roomId: cachedRoomId,
      })
      void this.fetchChat(chat.id).then((fetched: IChat) => {
        if (isOpenChatStale() || this.currentChat?.id !== chat.id) {
          return
        }
        let merged = fetched
        if (isTopicsModeChat(chat) && !isTopicsModeChat(merged)) {
          merged = {
            ...merged,
            discussion_mode: 'topics',
            topics: merged.topics?.length ? merged.topics : (chat.topics ?? []),
          }
        }
        this.setCurrentChatPreservingMemberCount(merged)
        this.upsertChatInList(merged)
        syncViewWithCore()
      }).catch(() => {
        // keep cached chat for messaging
      })
    } else {
      try {
        topicsDebugLog('core · openChat · fetchChat → start', { chatId: chat.id })
        activeChat = await this.fetchChat(chat.id)
        if (isOpenChatStale()) {
          topicsDebugLog('core · openChat · abort after fetchChat (stale)', {
            chatId: activeChat.id,
            requestSeq,
            currentSeq: openChatRequestSeq,
          })
          return
        }
        topicsDebugLog('core · openChat · fetchChat → done', {
          chatId: activeChat.id,
          topicsCount: activeChat.topics?.length ?? 0,
        })
      } catch (error) {
        if (isOpenChatStale()) {
          return
        }
        topicsDebugLog('core · openChat · fetchChat → error', {
          chatId: chat.id,
          error: String(error),
        })
        if (!activeChat.matrix_room_id) {
          try {
            activeChat = await fetchChatFromMatrix(activeChat.id, activeChat)
            this.upsertChatInList(activeChat)
          } catch {
            // Комната ещё не доступна
          }
        }
      }
    }

    if (isOpenChatStale()) {
      topicsDebugLog('core · openChat · abort before set currentChat (stale)', {
        chatId: activeChat.id,
        requestSeq,
      })
      return
    }

    // Не теряем topics-режим при неполном ответе fetchChat
    if (isTopicsModeChat(chat) && !isTopicsModeChat(activeChat)) {
      activeChat = {
        ...activeChat,
        discussion_mode: 'topics',
        topics: activeChat.topics?.length ? activeChat.topics : (chat.topics ?? []),
      }
    }

    this.setCurrentChatPreservingMemberCount(activeChat)
    this.pendingReactionOverrides = {}

    if (!activeChat.matrix_room_id?.trim()) {
      const roomId = chat.matrix_room_id?.trim()
          || this.chats.find(item => item.id === activeChat.id)?.matrix_room_id?.trim()
      if (roomId) {
        activeChat = { ...activeChat, matrix_room_id: roomId }
        this.setCurrentChatPreservingMemberCount(activeChat)
        this.upsertChatInList(activeChat)
      }
    }

    const isGroupLike = isGroupLikeChatType(activeChat.type)
        || isEventChatItem(activeChat)
    if (isGroupLike && !isSyntheticMatrixChatId(activeChat.id, { matrix_room_id: activeChat.matrix_room_id })) {
      void this.refreshChatMemberCount(activeChat.id)
    }

    const keepActiveTopic = isTopicsModeChat(activeChat)
        && topicBelongsToChat(this.activeTopic, activeChat)

    topicsDebugLog('core · openChat · after fetchChat state', {
      chatId: activeChat.id,
      keepActiveTopic,
      activeTopic: summarizeTopicForLog(this.activeTopic),
      showTopicsList: this.showTopicsList,
      messagesCount: this.messages.length,
    })

    if (!keepActiveTopic) {
      this.activeTopic = null
      clearMessages(this, { preserveRoomId: previousRoomId })
    } else {
      this.showTopicsList = false
    }

    try {
      localStorage.setItem('selectedChatIdFast', String(activeChat.id))
    } catch {
    }

    void this.fetchReactionTypes()

    if (isTopicsModeChat(activeChat)) {
      try {
        const topics = canOpenWithoutAwaitingFetchChat
          ? (activeChat.topics ?? [])
          : await ChatApiService.fetchTopics(activeChat.id)

        if (canOpenWithoutAwaitingFetchChat) {
          void ChatApiService.fetchTopics(activeChat.id).then(fetchedTopics => {
            if (isOpenChatStale() || this.currentChat?.id !== activeChat.id) {
              return
            }
            if (fetchedTopics.length > 0 || !this.currentChat.topics?.length) {
              const updated = {
                ...this.currentChat,
                topics: fetchedTopics,
                discussion_mode: 'topics' as const,
              }
              this.setCurrentChatPreservingMemberCount(updated)
              this.upsertChatInList(updated)
              this.syncTopicsUnreadFromMatrix(updated.id)
              syncViewWithCore()
            }
          }).catch(() => {
            // keep cached topics
          })
        }

        // Не затираем уже показанные темы пустым ответом (гонка / краткий provisioning)
        if (topics.length > 0 || !activeChat.topics?.length) {
          activeChat = {
            ...activeChat,
            topics,
            discussion_mode: 'topics',
            total_members: preserveTotalMembers(
              activeChat.total_members,
              this.currentChat?.total_members
                ?? this.chats.find(c => sameChatId(c.id, activeChat.id))?.total_members,
            ),
          }
          this.setCurrentChatPreservingMemberCount(activeChat)
          this.upsertChatInList(activeChat)
          this.syncTopicsUnreadFromMatrix(activeChat.id)
        }
      } catch (error) {
        logger.error('chat_openChat_topics_error', {
          file: 'chatStore',
          function: 'openChat',
          condition: String(error),
        })
      }

      if (keepActiveTopic) {
        if (this.messages.length === 0) {
          topicsDebugLog('core · openChat · fetchMessages (keepActiveTopic)')
          await this.fetchMessages(activeChat.id, {
            roomId: this.activeTopic?.matrix_room_id ?? undefined,
          })
        }

        syncViewWithCore()
        this.subscribeActiveRoomTimeline()
        await this.hydrateOutboxMessagesForCurrentChat()
        void this.flushMessageOutbox()
        topicsDebugLog('core · openChat · done (topics + keepActiveTopic)', {
          chatId: activeChat.id,
          messagesCount: this.messages.length,
          activeTopic: summarizeTopicForLog(this.activeTopic),
        })
        return
      }

      const tabs = isTopicsTabsDisplay(activeChat)
      this.selectedTopicsGroupId = tabs ? null : activeChat.id
      this.showTopicsList = !tabs
      const generalTopic = findGeneralTopic(activeChat)
      if (generalTopic?.matrix_room_id?.trim()) {
        topicsDebugLog('core · openChat · open general topic', {
          chatId: activeChat.id,
          topic: summarizeTopicForLog(generalTopic),
        })
        await this.openTopic(generalTopic)
        if (isOpenChatStale()) {
          return
        }
        topicsDebugLog('core · openChat · done (topics + general)', {
          chatId: activeChat.id,
          messagesCount: this.messages.length,
          activeTopic: summarizeTopicForLog(this.activeTopic),
        })
        return
      }

      // «Общий» ещё не готов — в list ждём выбор темы; в tabs — без сайдбар-панели
      this.activeTopic = null
      clearMatrixSubscription('room')
      syncViewWithCore()
      topicsDebugLog('core · openChat · done (topics placeholder, no general)', {
        chatId: activeChat.id,
        topicsCount: activeChat.topics?.length ?? 0,
      })
      return
    }

    this.showTopicsList = false
    topicsDebugLog('core · openChat · regular chat → fetchMessages')

    const roomId = activeChat.matrix_room_id?.trim()
    const paintedFromCache = Boolean(roomId && paintRoomTimelineFromMemory(this, roomId))

    if (paintedFromCache) {
      this.subscribeActiveRoomTimeline()
      void this.hydrateOutboxMessagesForCurrentChat()
      void this.flushMessageOutbox()
    }

    const markLastReadFromTimeline = () => {
      if (this.messages.length === 0) {
        return
      }
      try {
        const lastMessage = this.messages[this.messages.length - 1]
        const chatIndex = this.chats.findIndex(c => c.id === activeChat.id)
        if (chatIndex === -1) {
          return
        }
        const updatedChats = [...this.chats]
        updatedChats[chatIndex] = {
          ...updatedChats[chatIndex],
          last_read_message_id: lastMessage.id,
          last_read_matrix_event_id: lastMessage.matrix_event_id,
        }
        this.chats = updatedChats
      } catch {
      }
    }

    const loadLiveMessages = async () => {
      let membershipEnsuredRoomId: string | undefined
      if (roomId) {
        const joined = await ensureMatrixChatMembership({
          chatId: activeChat.id,
          roomId,
          chat: activeChat,
        })
        if (isOpenChatStale()) {
          return
        }
        if (joined) {
          membershipEnsuredRoomId = roomId
        }
      }

      await this.fetchMessages(activeChat.id, {
        ...(paintedFromCache ? { mergeWithExisting: true } : {}),
        membershipEnsuredRoomId,
      })
      if (isOpenChatStale() || this.currentChat?.id !== activeChat.id) {
        topicsDebugLog('core · openChat · abort after fetchMessages (stale)', {
          chatId: activeChat.id,
          requestSeq,
          currentChatId: this.currentChat?.id ?? null,
        })
        return
      }

      if (!paintedFromCache) {
        this.subscribeActiveRoomTimeline()
        await this.hydrateOutboxMessagesForCurrentChat()
        void this.flushMessageOutbox()
      }
      markLastReadFromTimeline()
    }

    if (paintedFromCache) {
      void loadLiveMessages()
      return
    }

    await loadLiveMessages()
  },

  async openTopic(topic: IChatTopic): Promise<void> {
    topicsDebugLog('core · openTopic · start', {
      topic: summarizeTopicForLog(topic),
      currentChatId: this.currentChat?.id ?? null,
      prevActiveTopic: summarizeTopicForLog(this.activeTopic),
      messagesCount: this.messages.length,
    })

    if (topic.archived) {
      topicsDebugLog('core · openTopic · abort (archived)', {
        topicId: topic.id,
        matrixRoomId: topic.matrix_room_id ?? null,
      })
      useFeedbackStore().showToast({
        type: 'info',
        message: 'Тема в архиве',
        time: 3000,
      })
      return
    }

    if (!this.currentChat || !isTopicOpenable(topic)) {
      topicsDebugLog('core · openTopic · abort (not ready)', {
        hasCurrentChat: Boolean(this.currentChat),
        matrixRoomId: topic.matrix_room_id ?? null,
        archived: topic.archived,
      })
      if (this.currentChat && topic.id && !topic.matrix_room_id && !topic.archived) {
        void ChatApiService.retryTopicProvision(this.currentChat.id, topic.id)
          .catch(() => undefined)
      }
      useFeedbackStore().showToast({
        type: 'error',
        message: 'Тема ещё не готова, попробуйте через пару секунд',
        time: 5000,
      })
      return
    }

    const roomId = topic.matrix_room_id.trim()
    const chatId = this.currentChat.id
    const generation = ++openTopicGeneration
    const parentRoomId = this.currentChat.matrix_room_id?.trim() || ''
    const previousRoomId = resolveActiveMessageRoomId(this.$state)
    // Satellite topic rooms are rarely already joined — skip soft wait, Django join.
    const forceRepair = Boolean(parentRoomId) && roomId !== parentRoomId

    // Topic rooms may have been leave+forgotten by prune (catalog used to omit them).
    if (isForgottenRoom(roomId)) {
      restoreForgottenRoom(roomId)
    }

    clearMatrixSubscription('room')
    this.activeTopic = { ...topic, matrix_room_id: roomId }
    this.showTopicsList = false
    this.pendingReactionOverrides = {}
    clearMessages(this, { preserveRoomId: previousRoomId })
    const paintedFromCache = paintRoomTimelineFromMemory(this, roomId)
    if (paintedFromCache) {
      this.subscribeActiveRoomTimeline()
      void this.hydrateOutboxMessagesForCurrentChat()
      void this.flushMessageOutbox()
    }

    const isTopicOpenStale = () => (
      generation !== openTopicGeneration || this.activeTopic?.matrix_room_id !== roomId
    )

    const loadTopicMessages = async () => {
      topicsDebugLog('core · openTopic · ensure membership', {
        chatId,
        roomId,
        forceRepair,
        generation,
      })
      const joined = await ensureMatrixChatMembership({
        chatId,
        roomId,
        chat: this.currentChat,
        forceRepair,
      })

      if (isTopicOpenStale()) {
        topicsDebugLog('core · openTopic · abort after ensure (superseded)', {
          expectedRoomId: roomId,
          actualRoomId: this.activeTopic?.matrix_room_id ?? null,
          generation,
          openTopicGeneration,
          joined,
        })
        return
      }

      if (!joined && !paintedFromCache) {
        useFeedbackStore().showToast({
          type: 'warning',
          message: 'Подключаем к комнате… Попробуйте ещё раз через пару секунд',
          time: 4000,
        })
      }

      topicsDebugLog('core · openTopic · fetchMessages → start', {
        chatId,
        roomId,
        matrixUnreadBefore: countMatrixRoomUnread(roomId),
        joined,
        generation,
      })
      await this.fetchMessages(chatId, {
        roomId,
        mergeWithExisting: paintedFromCache,
      })

      if (isTopicOpenStale()) {
        topicsDebugLog('core · openTopic · abort after fetch (topic changed)', {
          expectedRoomId: roomId,
          actualRoomId: this.activeTopic?.matrix_room_id ?? null,
          messagesCount: this.messages.length,
          generation,
          openTopicGeneration,
        })
        return
      }

      if (this.activeTopic && !shouldSuppressAutoReadReceipt()) {
        this.activeTopic = { ...this.activeTopic, unread_count: 0 }
      }
      this.syncTopicsUnreadFromMatrix(chatId)

      if (!paintedFromCache) {
        this.subscribeActiveRoomTimeline()
        await this.hydrateOutboxMessagesForCurrentChat()
        void this.flushMessageOutbox()
      }
      syncViewWithCore()
      topicsDebugLog('core · openTopic · done', {
        topic: summarizeTopicForLog(this.activeTopic),
        messagesCount: this.messages.length,
        activeMessageRoomId: resolveActiveMessageRoomId(this.$state) ?? null,
        matrixUnreadAfter: countMatrixRoomUnread(roomId),
        unreadBreakdown: this.currentChat
          ? buildTopicsUnreadDebugBreakdown(this.currentChat, {
            isActiveTopicId: this.activeTopic?.id ?? null,
          })
          : null,
      })
    }

    if (paintedFromCache) {
      void loadTopicMessages()
      return
    }

    await loadTopicMessages()
  },

  async backToTopicsList(): Promise<void> {
    topicsDebugLog('core · backToTopicsList · start', {
      currentChatId: this.currentChat?.id ?? null,
      activeTopic: summarizeTopicForLog(this.activeTopic),
    })
    if (!this.currentChat || !isTopicsModeChat(this.currentChat)) {
      return
    }

    const previousRoomId = resolveActiveMessageRoomId(this.$state)
    clearMatrixSubscription('room')
    this.activeTopic = null
    this.showTopicsList = true
    clearMessages(this, { preserveRoomId: previousRoomId })
    syncViewWithCore()

    try {
      const topics = await ChatApiService.fetchTopics(this.currentChat.id)
      const updated = { ...this.currentChat, topics }
      this.currentChat = updated
      this.upsertChatInList(updated)
      this.syncTopicsUnreadFromMatrix(updated.id)
    } catch {
      // keep cached topics
    }

    topicsDebugLog('core · backToTopicsList · done', {
      currentChatId: this.currentChat?.id ?? null,
      topicsCount: this.currentChat?.topics?.length ?? 0,
      showTopicsList: this.showTopicsList,
    })
  },

  /** Закрыть панель тем и выйти из topics-навигации в общий список */
  closeTopicsNavigation(): void {
    topicsDebugLog('core · closeTopicsNavigation', {
      currentChatId: this.currentChat?.id ?? null,
      activeTopic: summarizeTopicForLog(this.activeTopic),
    })
    const previousRoomId = resolveActiveMessageRoomId(this.$state)
    this.activeTopic = null
    this.selectedTopicsGroupId = null
    this.showTopicsList = false
    clearMessages(this, { preserveRoomId: previousRoomId })
    clearMatrixSubscription('room')
  },

  async enableTopicsForCurrentChat(): Promise<void> {
    const chat = this.currentChat
    if (!chat) {
      return
    }

    const previousRoomId = resolveActiveMessageRoomId(this.$state)
    const refreshed = await ChatApiService.enableTopics(chat.id)
    this.currentChat = { ...chat, ...refreshed }
    this.upsertChatInList(this.currentChat)
    this.showTopicsList = true
    this.activeTopic = null
    clearMessages(this, { preserveRoomId: previousRoomId })
    clearMatrixSubscription('room')

    useFeedbackStore().showToast({
      type: 'success',
      message: 'Темы включены',
      time: 3000,
    })
  },

  async createTopic(title: string): Promise<IChatTopic | null> {
    const chat = this.currentChat
    if (!chat) {
      return null
    }

    const topic = await ChatApiService.createTopic(chat.id, title)
    const topics = await ChatApiService.fetchTopics(chat.id)
    const updated = { ...chat, topics, discussion_mode: 'topics' as const }
    this.currentChat = updated
    this.upsertChatInList(updated)
    return topic
  },

  async updateTopic(topicId: number, title: string): Promise<IChatTopic | null> {
    const chat = this.currentChat
    if (!chat) {
      return null
    }

    const topic = await ChatApiService.updateTopic(chat.id, topicId, title)
    const topics = (chat.topics ?? []).map(item =>
      item.id === topicId ? { ...item, ...topic, title: topic.title || title } : item,
    )
    const updated = { ...chat, topics }
    this.currentChat = updated
    this.upsertChatInList(updated)
    if (this.activeTopic?.id === topicId) {
      this.activeTopic = { ...this.activeTopic, ...topic, title: topic.title || title }
    }
    syncViewWithCore()
    return topic
  },

  async archiveTopic(topicId: number): Promise<void> {
    const chat = this.currentChat
    if (!chat) {
      return
    }

    await ChatApiService.archiveTopic(chat.id, topicId)

    if (this.activeTopic?.id === topicId) {
      await this.backToTopicsList()
    } else {
      const topics = await ChatApiService.fetchTopics(chat.id)
      const updated = { ...chat, topics }
      this.currentChat = updated
      this.upsertChatInList(updated)
    }

    syncViewWithCore()
  },
})
